import React from 'react';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import FinInfoPopover from '../../connect/finance/fin-info-popover';

// Frame 2c's Summary: Count and Net Amount, and nothing else. The mock's own
// DEV NOTE forbids an aggregated Gross or Fees column for the per-order
// processing fees — Charges stays net of them, and the per-order rows below
// answer that question honestly (spec 2026-09-22 §6). The owner's
// 2026-10-07 ask is a different thing: a dispute's fee and an instant
// payout's fee are not per-order processing fees, and each is its own line
// when it is not zero.
//
// Total Payout is the payout's own amount, never the sum of the lines
// — the API reconciles them, logs a mismatch and sends the payout amount,
// because that is the figure the bank will show.
const GRID = 'grid grid-cols-[minmax(0,1fr)_72px_120px] gap-x-3 px-6';

// The API's own lines (2026-10-07): Stripe's classification of every
// balance transaction, sent only when a line carries something, Charges
// always. The words and the explanations live here, by key; a key this
// build does not know draws the API's own label and no explanation.
const LINE_COPY = {
  charges: { label: 'Charges', hint: 'Total charges from completed orders included in this payout. Funds may still be pending.' },
  refunds: { label: 'Refunds', hint: 'Total amount refunded to customers.' },
  disputes: { label: 'Disputes', hint: 'Payments a customer disputed with their bank. Stripe takes the amount back while the dispute is open.' },
  dispute_reversals: { label: 'Dispute reversals', hint: 'Disputed amounts returned to you after a dispute was won.' },
  dispute_fees: { label: 'Dispute fees', hint: "Stripe's fee for each dispute, returned when the dispute is won." },
  payouts_returned: { label: 'Payouts returned', hint: 'Money from a payout that failed, back in your balance.' },
  payouts: { label: 'Payouts', hint: 'Payouts you sent yourself since the last automatic one, already taken out of this money.' },
  instant_payout_fees: { label: 'Instant payout fees', hint: "Stripe's fee for the instant payouts you sent." },
  stripe_fees: { label: 'Stripe fees', hint: 'Stripe fees not tied to one order, such as monthly account fees.' },
  transfers: { label: 'Transfers', hint: 'Money moved between your balance and your platform or bank, such as a top-up.' },
  adjustments: { label: 'Adjustments', hint: 'Other corrections Stripe applied to your balance, such as a manual adjustment.' },
};

// What the lines cannot explain — an instant payout of pending funds, a
// Stripe reserve — is named rather than left as a gap in the arithmetic,
// and only when it is not zero. Not a set of transactions: no count.
const OTHER = {
  key: 'other',
  label: 'Other Stripe adjustments',
  hint: 'Amounts Stripe holds or releases that are not itemized as transactions, such as a reserve.',
  bare: true,
};

// An older API sends no `lines`: the three groups, as the page always drew
// them, plus the Upcoming page's two extra lines (owner, 2026-09-24) when
// they carry something.
const legacyRows = (summary) =>
  [
    { key: 'charges', ...LINE_COPY.charges, count: summary?.charges?.count || 0, net: summary?.charges?.net || 0 },
    { key: 'refunds', ...LINE_COPY.refunds, count: summary?.refunds?.count || 0, net: summary?.refunds?.net || 0 },
    { key: 'adjustments', label: 'Adjustments', hint: 'Manual corrections applied by Thisway, such as dispute reversals or fee adjustments.', count: summary?.adjustments?.count || 0, net: summary?.adjustments?.net || 0 },
    (summary?.paidOut?.count || 0) > 0
      ? { key: 'paidOut', ...LINE_COPY.payouts, count: summary.paidOut.count, net: summary.paidOut.net || 0 }
      : null,
  ].filter(Boolean);

// The API's lines: only those that carry something (the API already drops
// the rest; this is the same rule, kept here so the card never draws a
// zero line whatever it is sent), and Charges first even if it was not sent.
const apiRows = (lines) => {
  const kept = lines
    .filter((line) => line && (line.key === 'charges' || (line.count || 0) > 0 || (line.net || 0) !== 0))
    .map((line) => ({
      key: line.key,
      label: LINE_COPY[line.key]?.label || line.label || line.key,
      hint: LINE_COPY[line.key]?.hint,
      count: line.count || 0,
      net: line.net || 0,
    }));
  return kept.some((row) => row.key === 'charges')
    ? kept
    : [{ key: 'charges', ...LINE_COPY.charges, count: 0, net: 0 }, ...kept];
};

export const summaryRows = (summary) => {
  const rows = Array.isArray(summary?.lines) ? apiRows(summary.lines) : legacyRows(summary);
  return summary?.other ? [...rows, { ...OTHER, net: summary.other }] : rows;
};

// A deduction reads as a deduction: parentheses, the same signed-net idiom
// order-report-columns.js's `net()` uses. Not red (owner, 2026-09-23): a
// refund is an ordinary part of a payout, not an alarm, so it reads in the
// same ink as the other rows.
const net = (value) =>
  value < 0 ? (
    <span className="font-semibold tabular-nums text-fin-ink">{`(${formatCurrency(Math.abs(value))})`}</span>
  ) : (
    <span className="font-semibold tabular-nums text-fin-ink">{formatCurrency(value || 0)}</span>
  );

const PayoutSummaryCard = ({ summary }) => (
  <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
    <div className="px-6 pt-[18px]">
      <h2 className="text-[15px] font-bold tracking-[-0.01em] text-fin-ink">Summary</h2>
      <p className="mt-1 text-[13px] text-fin-muted">
        Net amounts after processing. Per-order breakdown is in Transactions below.
      </p>
    </div>
    <div role="table" aria-label="Payout summary">
      <div role="row" className={cn(GRID, 'border-b border-fin-border pb-2.5 pt-3.5 text-[13.5px] font-bold text-fin-ink')}>
        <div role="columnheader" />
        <div role="columnheader" className="text-right">Count</div>
        <div role="columnheader" className="text-right">Net Amount</div>
      </div>
      <div role="rowgroup">
        {summaryRows(summary).map(({ key, label, hint, count, net: value, bare }) => (
          <div key={key} role="row" className={cn(GRID, 'items-center border-b border-fin-hair py-[13px] text-[13.5px]')}>
            <div role="cell" className="flex items-center gap-1.5 font-semibold text-fin-ink">
              {label}
              {/* The same popover as Overview's Pending (owner, 2026-10-07): a
                  bare title= tooltip arrived seconds late and never on touch.
                  None for a line this build has no words for. */}
              {hint ? <FinInfoPopover ariaLabel={`About ${label}`} text={hint} /> : null}
            </div>
            <div role="cell" className="text-right tabular-nums text-fin-muted-2">
              {bare ? null : count}
            </div>
            <div role="cell" className="text-right">{net(value)}</div>
          </div>
        ))}
        <div role="row" className={cn(GRID, 'items-center bg-fin-recess pb-4 pt-3.5 text-[13.5px]')}>
          <div role="cell" className="font-extrabold text-fin-ink">Total Payout</div>
          <div role="cell" />
          <div role="cell" className="text-right font-extrabold tabular-nums text-fin-ink">
            {formatCurrency(summary?.total || 0)}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default PayoutSummaryCard;
